import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["result"]
  static values = { url: String }

  optimize(event) {
    event.preventDefault()
    const button = event.currentTarget
    const originalText = button.innerHTML
    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Calculando...'

    const url = this.urlValue
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Preview mode: don't apply yet
    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => response.json().then(data => ({ ok: response.ok, data })))
    .then(({ ok, data }) => {
      if (ok && data.success) {
        this.showPreview(data)
      } else {
        this.showError(data.error || "Error al optimizar la ruta")
      }
    })
    .catch(() => {
      this.showError("Error de conexión al optimizar la ruta")
    })
    .finally(() => {
      button.disabled = false
      button.innerHTML = originalText
    })
  }

  apply(event) {
    event.preventDefault()
    const button = event.currentTarget
    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Aplicando...'

    // Add apply=true to the URL
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('apply', 'true')
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => response.json().then(data => ({ ok: response.ok, data })))
    .then(({ ok, data }) => {
      if (ok && data.success) {
        this.showApplied(data)
        setTimeout(() => window.location.reload(), 1500)
      } else {
        this.showError(data.error || "Error al aplicar la optimización")
      }
    })
    .catch(() => {
      this.showError("Error de conexión al aplicar la optimización")
    })
  }

  showPreview(data) {
    const resultEl = this.resultTarget
    let stopsHtml = ""
    if (data.stops && data.stops.length > 0) {
      stopsHtml = '<ol class="mb-0 mt-2 small">' +
        data.stops.map(stop =>
          `<li><strong>${stop.patient}</strong> - ${stop.address || 'Sin dirección'} <span class="text-muted">(${stop.zone || 'Sin zona'})</span></li>`
        ).join('') +
        '</ol>'
    }

    // Build comparison table if we have current route data
    let comparisonHtml = ""
    if (data.current_distance_km != null) {
      const savedKm = (data.current_distance_km - data.distance_km).toFixed(1)
      const savedClass = savedKm > 0 ? 'text-success' : 'text-danger'
      const savedSign = savedKm > 0 ? '-' : '+'

      comparisonHtml = `
        <table class="table table-sm table-bordered mt-2 mb-2" style="max-width: 400px;">
          <thead class="thead-light">
            <tr>
              <th></th>
              <th>Actual</th>
              <th>Optimizado</th>
              <th>Diferencia</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Distancia</strong></td>
              <td>${data.current_distance_km} km</td>
              <td>${data.distance_km} km</td>
              <td class="${savedClass}"><strong>${savedSign}${Math.abs(savedKm)} km</strong></td>
            </tr>
            <tr>
              <td><strong>Tiempo</strong></td>
              <td>${data.current_duration_text}</td>
              <td>${data.duration_text}</td>
              <td class="${savedClass}"><strong>${data.duration_text}</strong></td>
            </tr>
          </tbody>
        </table>
      `
    } else {
      comparisonHtml = `
        <div class="mt-2">
          <span class="mr-3"><strong>Distancia:</strong> ${data.distance_km} km</span>
          <span><strong>Tiempo:</strong> ${data.duration_text}</span>
        </div>
      `
    }

    // Show confirmation buttons only if this is for a specific supplier
    const hasSupplier = data.current_distance_km != null
    const buttonsHtml = hasSupplier ? `
      <div class="mt-3">
        <button class="btn btn-success btn-sm mr-2" data-action="click->optimize-route#apply">
          <i class="fas fa-check mr-1"></i> Aplicar orden optimizado
        </button>
        <button class="btn btn-outline-secondary btn-sm" data-action="click->optimize-route#dismiss">
          Cancelar
        </button>
      </div>
    ` : ''

    resultEl.innerHTML = `
      <div class="alert alert-info mt-3">
        <strong>Ruta optimizada</strong> - ${data.stops_count} paradas
        ${comparisonHtml}
        <details class="mt-2">
          <summary class="small text-muted" style="cursor: pointer;">Ver orden propuesto</summary>
          ${stopsHtml}
        </details>
        ${buttonsHtml}
      </div>
    `
  }

  showApplied(data) {
    const resultEl = this.resultTarget
    resultEl.innerHTML = `
      <div class="alert alert-success mt-3">
        <i class="fas fa-check-circle mr-2"></i>
        <strong>Orden optimizado aplicado.</strong> Recargando...
      </div>
    `
  }

  dismiss(event) {
    event.preventDefault()
    this.resultTarget.innerHTML = ""
  }

  showError(message) {
    const resultEl = this.resultTarget
    resultEl.innerHTML = `
      <div class="alert alert-danger mt-3">
        <strong>Error:</strong> ${message}
      </div>
    `
  }
}
